<template>
  <div
    class="fill-height of-y"
    style="position: relative"
  >

    <v-scroll-y-transition hide-on-leave>
      <v-stepper
        v-if="!verified"
        v-model="step"
        dark
        vertical
        class="black fill-height"
        style="background: rgba(255,255,255,0) !important;"
      >
        <!--User Info-->
        <v-stepper-step :complete="userInfoDone" step="1">User Information</v-stepper-step>
        <v-stepper-content step="1" class="pt-0">

          <div style="max-width: 450px" class="pb-2">
            <v-form ref="userInfo" @submit="$event.preventDefault()">
              <!--Full Name-->
              <v-text-field
                v-model="signup.name"
                class="pb-2"
                outlined
                dense
                prepend-icon="mdi-account"
                placeholder="Full Name"
                hide-details="auto"
                color="primary"
                dark
                :rules="[validationRules.required]"
              />
              <!--Your Email-->
              <v-text-field
                v-model="signup.email"
                class="pb-2"
                outlined
                dense
                prepend-icon="mdi-at"
                placeholder="Your Email"
                hide-details="auto"
                color="primary"
                dark
                :rules="[validationRules.required, validationRules.email]"
              />
              <!--Contact Number-->
              <v-text-field
                v-model="signup.contactNumber"
                class="pb-2"
                outlined
                dense
                prepend-icon="mdi-phone"
                placeholder="Contact Number"
                hide-details="auto"
                color="primary"
                dark
                :rules="[validationRules.required, validationRules.cellNo]"
              />
            </v-form>
          </div>

          <v-btn
            color="primary"
            small
            @click="checkUser()"
          >
            Continue
          </v-btn>
        </v-stepper-content>
        <!--Company Info-->
        <v-stepper-step :complete="companyInfoDone" step="2">Company Information</v-stepper-step>
        <v-stepper-content step="2" class="pt-0">

          <div style="max-width: 450px" class="pb-2">
            <v-form ref="companyInfo" @submit="$event.preventDefault()">
              <!--Company Name-->
              <v-text-field
                v-model="signup.companyName"
                class="pb-2"
                outlined
                dense
                prepend-icon="mdi-domain"
                placeholder="Company Name"
                hide-details="auto"
                color="primary"
                dark
                :rules="[validationRules.required]"
                :error-messages="companyErrors"
              />
              <!--Address-->
              <v-text-field
                v-model="signup.address.line1"
                class="pb-2"
                outlined
                dense
                prepend-icon="mdi-map-marker"
                placeholder="Address Line 1"
                hide-details="auto"
                color="primary"
                dark
                :rules="[validationRules.required]"
              />
              <v-text-field
                v-model="signup.address.line2"
                class="pb-2"
                outlined
                dense
                prepend-icon="mdi-map-marker"
                placeholder="Address Line 2"
                hide-details="auto"
                color="primary"
                dark
              />
              <v-combobox
                v-model="city"
                class="pb-2"
                outlined
                dense
                prepend-icon="mdi-map-marker"
                placeholder="City / Town"
                hide-details="auto"
                color="primary"
                dark
                :rules="[validationRules.required]"
                :items="towns"
              />
              <v-combobox
                v-model="signup.address.region"
                class="pb-2"
                outlined
                dense
                prepend-icon="mdi-map-marker"
                placeholder="Region"
                hide-details="auto"
                color="primary"
                dark
                :rules="[validationRules.required]"
                :items="provinces"
              />
              <v-text-field
                v-model="signup.address.areaCode"
                class="pb-2"
                outlined
                dense
                prepend-icon="mdi-map-marker"
                placeholder="Area Code"
                hide-details="auto"
                color="primary"
                dark
                :rules="[validationRules.required]"
              />
            </v-form>
          </div>


          <v-btn
            color="primary"
            small outlined
            @click="step = 1"
            :disabled="checkingCompany"
          >
            Back
          </v-btn>


          <v-btn
            color="primary"
            small
            class="ml-2"
            @click="checkCompany()"
            :disabled="checkingCompany"
            :loading="checkingCompany"
          >
            Continue
          </v-btn>
        </v-stepper-content>
        <!--Billing-->
        <v-stepper-step :complete="verificationDone" step="3">Verification</v-stepper-step>
        <v-stepper-content step="3" class="pt-0">
          <div style="max-width: 450px" class="pb-2">

            <div class="text-h6 font-weight-light pb-2">
              Almost Done!
            </div>

            <div class="text-body-2 pb-2">
              We have sent a verification code to <strong>{{signup.email}}</strong>
            </div>

            <div class="text-body-2 pb-2">
              Please enter the code below
            </div>

            <v-otp-input
              style="width: 300px"
              v-model="verificationOtp"
              class="pb-2"
              outlined
              dense
              hide-details="auto"
              color="primary"
              dark
            />

            <div v-if="!!verifyError" class="error--text overline pb-2" style="line-height: 1">
              {{verifyError}}
            </div>

          </div>

          <v-btn
            color="primary"
            small outlined
            @click="step = 2"
            :disabled="verifying"
          >
            Back
          </v-btn>

          <v-btn
            class="ml-2"
            color="primary"
            small
            @click="verify()"
            :loading="verifying"
            :disabled="verifying"
          >
            Verify Account
          </v-btn>
        </v-stepper-content>
      </v-stepper>
    </v-scroll-y-transition>

  </div>


</template>

<script>
import {nameSort, validationRules} from '@/core/codeFunctions'

const places = [
  {province: "Eastern Cape", places: [ "Alice", "Aliwal North", "Bhisho", "Burgersdorp", "Butterworth", "Cradock", "Dimbaza", "East London", "Elliot", "Fort Beaufort", "Gonubie", "Graaff-Reinet", "Grahamstown", "Humansdorp", "Jeffreys Bay", "King William's Town", "Mdantsane", "Middelburg", "Umtata", "Ndevana", "Port Alfred", "Port Elizabeth", "Queenstown", "Somerset East", "Stutterheim", "Uitenhage", "Whittlesea", ]},
  {province: "Free State", places: [ "Allanridge", "Bethlehem", "Bloemfontein", "Bothaville", "Botshabelo", "Bultfontein", "Clocolan", "Deneysville", "Ficksburg", "Frankfort", "Harrismith", "Heilbron", "Hoopstad", "Kroonstad", "Ladybrand", "Marquard", "Odendaalsrus", "Parys", "Phomolong", "Phuthaditjhaba", "Reitz", "Sasolburg", "Senekal", "Thaba Nchu", "Theunissen", "Tshiame", "Viljoenskroon", "Villiers", "Virginia", "Vrede", "Welkom", "Wesselsbron", "Zastron", ]},
  {province: "Gauteng", places: [ "Bronkhorstspruit", "Carletonville", "Cullinan", "Ekangala", "Evaton", "Fochville", "Hammanskraal", "Heidelberg", "Johannesburg", "Nigel", "Pretoria", "Soshanguve", "Vereeniging", "Westonaria", ]},
  {province: "KwaZulu-Natal", places: [ "Ballito", "Clydesdale", "Dundee", "Durban", "Empangeni", "Estcourt", "Folweni", "Frischgewaagd", "Glencoe", "Greytown", "Howick", "Kokstad", "Ladysmith", "Mandini", "Margate", "Mondlo", "Mooi Rivier", "Mpophomeni", "Mpumalanga", "Mtubatuba", "Ncotshane", "Newcastle", "Pietermaritzburg", "Port Shepstone", "Richards Bay", "Shakaskraal", "Stanger", "Tongaat", "Ulundi", "Umzinto", "Vryheid", "Wembesi", ]},
  {province: "Limpopo", places: [ "Amandelbult Mine", "Warmbad", "Lebowakgomo", "Ellisras", "Louis Trichardt", "Mankweng", "Marapong", "Modimolle", "Potgietersrus", "Naboomspruit", "Musina", "Nkowankowa", "Phalaborwa", "Polokwane", "Praktiseer", "Thabazimbi", "Thohoyandou", "Tshitereke", "Vaalwater", "Tzaneen"]},
  {province: "Mpumalanga", places: [ "Balfour", "Barberton", "eMakhazeni", "Bethal", "Carolina", "Vlakpoort", "Delmas", "Dwarsloop", "Elukwatuni", "Embalenhle", "Piet Retief", "Ermelo", "Hendrina", "Kamaqhekeza", "Kinross", "Ga-Nala", "Kwaggafontein", "Leslie", "Lydenburg", "Middelburg", "Mkhuhlu", "Moloto", "Mbombela", "Phola", "Redhill", "Sabie", "Siyabuswa", "Standerton", "Tekwane", "Trichardt", "Tweefontein", "Vaalbank", "Verena", "Volksrust", "White River", "Witbank", ]},
  {province: "Northern Cape", places: [ "Barkly West", "Colesberg", "De Aar", "Douglas", "Jan Kempdorp", "Kimberley", "Postmasburg", "Ritchie", "Upington", "Warrenton", ]},
  {province: "North West", places: [ "Bloemhof", "Brits", "Christiana", "Coligny", "Hartbeestfontein", "Hartbeestfontein A", "Hartebeespoort", "Itsoseng", "Klerksdorp", "Koster", "Letlhabile", "Lichtenburg", "Mafikeng", "Rooikoppies", "Orkney", "Ottosdal", "Potchefstroom", "Rustenburg", "Schweizer-Reneke", "Stilfontein", "Ventersdorp", "Vryburg", "Wolmaransstad", "Zeerust", ]},
  {province: "Western Cape", places: [ "Atlantis", "Beaufort West", "Bredasdorp", "Cape Town", "Ceres", "George", "Grabouw", "Groot Brakrivier", "Hermanus", "Knysna", "Malmesbury", "Montagu", "Mossel Bay", "Oudtshoorn", "Paarl", "Plettenberg Bay", "Riversdale", "Robertson", "Saldanha", "Somerset West", "Stellenbosch", "Swellendam", "Vredenburg", "Vredendal", "Worcester", ]},
]

class SignupData {
  constructor(obj) {
    this.name = null;
    this.email = null;
    this.contactNumber = null;
    this.companyName = null;
    this.vatNo = null;
    this.partnerCode = null
    this.address = {
      line1: null,
      line2: null,
      region: null,
      city: null,
      areaCode: null
    }
    Object.assign(this, obj)
  }
}

export default {
  data() {
    return {
      validationRules: validationRules,
      signup: new SignupData({}),
      step: 1,
      userInfoDone: false,
      companyInfoDone: false,
      verificationDone: false,
      checkingCompany: false,
      companyErrors: null,
      verificationOtp: null,
      verifying: false,
      verifyError: null,
      verified: false,
      passwordResetSent: false
    }
  },
  methods: {
    checkUser() {
      if (this.$refs.userInfo.validate()) {
        this.userInfoDone = true;
        this.step = 2
      }
      // step = 2
    },
    async checkCompany() {
      try {
        if (!this.$refs.companyInfo.validate())
          return;
        this.companyErrors = null;
        this.checkingCompany = true;
        const {status} = await this.$newReq('POST', 'signup/checkSite', {
          companyName: this.signup.companyName,
          email: this.signup.email,
          name: this.signup.name
        }, false)
        if (status) {
          this.companyInfoDone = true;
          this.step = 3;
          this.verificationOtp = null
        } else {
          this.companyErrors = ['Looks like your company already exists, please contact support']
        }

      } catch (e) {
        this.$snack.netWorkError()
      } finally {
        this.checkingCompany = false;
      }
    },
    async verify() {
      try {
        this.verifyError = null;

        if (!this.verificationOtp || !(this.verificationOtp.length === 6))
          return this.verifyError = 'Verification code required';


        this.verifying = true;
        const {passwordResetSent, error} = await this.$newReq('POST', 'signup/verify', {
          ...this.signup,
          otp: this.verificationOtp,
          partnerCode: this.partnerCode
        }, false)

        if (!error) {
          this.verifying = false;
          this.verified = true;
          this.passwordResetSent = passwordResetSent

          this.$emit('siteVerified', true);
        } else {
          this.verifyError = error
        }
      } catch (e) {
        console.log(e)
        this.$snack.netWorkError()
      } finally {
        this.verifying = false;
      }
    }
  },
  watch: {
    "$store.state.signup": {
      immediate: true,
      handler(val) {
        if (val)
          this.signup = new SignupData({})
      }
    }
  },
  computed: {
    partnerCode() {
      try {
        return (new URL(window.location)).searchParams.get("signup") || null
      } catch (e) {
        console.log(e)
        return null
      }
    },

    city: {
      get() {
        return this.signup.address.city
      },
      set(val) {
        this.signup.address.region = null;
        this.signup.address.city = val;
        if (val)
          places.forEach(place => {
            if (place.places.find(town => town === val))
              this.signup.address.region = place.province
          })
      }
    },
    towns() {
      return places.reduce((list, place) => {
        return list.concat(place.places)
      }, []).sort(nameSort)
    },
    provinces() {
      return places.map(place => place.province).sort(nameSort)
    }
  },
}
</script>

<style scoped>
.login-bg {
  height: 100%;
  width: 100vw;
  margin: 0;
  padding: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 1) 0%, rgba(32, 32, 32, 1) 70%, rgba(45, 45, 45, 1) 70.1%, rgba(19, 19, 19, 1) 100%);
  font-weight: 200;
}
</style>
